<template>
  <v-dialog v-model="dialogVisible"
            max-width="900px"
            persistent
            scrollable>
    <v-card>
      <v-toolbar color="primary"
                 dark
                 flat
                 elevation="0">
        <v-toolbar-title class="text-h6">
          <v-icon class="mr-2">fa-sms</v-icon>
          Send Text Message to {{ recipientName }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon
               @click="closeDialog">
          <v-icon>fa-times</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Template Preview Section -->
      <template v-if="selectedTemplate">
        <v-card-text class="pt-2 pb-2">
          <v-chip color="primary"
                  text-color="white"
                  small
                  class="mb-4">
            <v-icon left small>fa-comment-alt</v-icon>
            Selected Template
          </v-chip>

          <div class="d-flex align-center mb-2">
            <v-icon color="grey darken-1" class="mr-2">fa-bookmark</v-icon>
            <span class="text-h6 font-weight-medium">{{ selectedTemplate.name }}</span>
            <v-spacer></v-spacer>
            <v-btn text
                   small
                   color="primary"
                   @click="clearTemplate">
              <v-icon left small>fa-arrow-left</v-icon>
              Change Template
            </v-btn>
          </div>

          <v-card outlined
                  class="template-preview">
            <v-card-text class="grey lighten-5">
              <div class="body-1" v-html="formatMessageText(selectedTemplate.content)"></div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </template>

      <!-- Template Selection Section -->
      <template v-else>
        <v-card-text class="pt-2 pb-2">
          <div class="d-flex align-center mb-3">
            <v-icon color="grey darken-1" class="mr-2">fa-comment-dots</v-icon>
            <span class="text-h6">Choose a Message Template</span>
            <v-spacer></v-spacer>
            <v-chip outlined small>
              {{ templates?.length || 0 }} templates
            </v-chip>
          </div>

          <v-card outlined>
            <v-list dense
                    class="template-list">
              <template v-for="(template, index) in templates">
                <v-list-item :key="template.id"
                             @click.prevent="selectTemplate(template)"
                             class="template-item">
                  <v-list-item-avatar>
                    <v-icon color="primary">fa-comment-alt</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ template.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="template-content">
                      <div v-html="formatMessageText(template.content)"></div>
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-icon color="grey">fa-chevron-right</v-icon>
                  </v-list-item-action>
                </v-list-item>

                <v-divider v-if="index < templates.length - 1" :key="`divider-${template.id}`"></v-divider>
              </template>
            </v-list>
          </v-card>
        </v-card-text>
      </template>

      <!-- File Sharing Section -->
      <template v-if="selectedTemplate?.has_shared_link_tag">
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <div class="d-flex align-center mb-3">
            <v-icon color="grey darken-1" class="mr-2">fa-paperclip</v-icon>
            <span class="text-h6">Select File to Share</span>
            <v-spacer></v-spacer>
            <v-chip outlined small color="orange">
              Required
            </v-chip>
          </div>

          <v-card outlined>
            <v-list dense
                    class="presentation-list">
              <template v-for="(presentation, index) in sharablePresentations">
                <v-list-item :key="presentation.id"
                             @click.prevent="selectPresentation(presentation)"
                             class="presentation-item"
                             :class="{'selected': selectedPresentation?.id === presentation.id}">
                  <v-list-item-avatar>
                    <v-icon :color="selectedPresentation?.id === presentation.id ? 'orange' : 'orange darken-1'">fa-file-pdf</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ presentation.name }}
                    </v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-icon v-if="selectedPresentation?.id === presentation.id" color="orange">fa-check-circle</v-icon>
                    <v-icon v-else color="grey">fa-chevron-right</v-icon>
                  </v-list-item-action>
                </v-list-item>

                <v-divider v-if="index < sharablePresentations.length - 1" :key="`divider-${presentation.id}`"></v-divider>
              </template>
            </v-list>
          </v-card>
        </v-card-text>
      </template>

      <!-- Action Buttons -->
      <v-card-actions class="px-6 py-4">
        <v-spacer></v-spacer>
        <v-btn text
               @click="closeDialog"
               class="mr-2">
          Cancel
        </v-btn>
        <v-btn color="primary"
               :disabled="!selectedTemplate || (selectedTemplate?.has_shared_link_tag && !selectedPresentation)"
               @click="confirmSelection">
          <v-icon left>fa-sms</v-icon>
          Send Message
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {isString} from 'lodash';
import DOMPurify from 'dompurify';

export default {
  name: 'SelectSinchTemplateDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    templates: {
      type: Array,
      default: () => []
    },
    sharablePresentations: {
      type: Array,
      default: () => []
    },
    recipientName: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      selectedTemplate: null,
      selectedPresentation: null
    }
  },

  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },

  watch: {
    value(isOpen) {
      if (isOpen) {
        this.resetSelection();
      }
    }
  },

  methods: {
    formatMessageText(text) {
      if (isString(text)) {
        return DOMPurify.sanitize(text.replace(/\r\n|\r|\n/g, '<br/>'));
      }
      return text;
    },

    selectTemplate(template) {
      this.selectedTemplate = template;
      this.$emit('template-selected', template);
    },

    selectPresentation(presentation) {
      this.selectedPresentation = presentation;
      this.$emit('presentation-selected', presentation);
    },

    clearTemplate() {
      this.selectedTemplate = null;
      this.selectedPresentation = null;
      this.$emit('template-cleared');
    },

    confirmSelection() {
      this.$emit('confirm', {
        template: this.selectedTemplate,
        presentation: this.selectedPresentation
      });
    },

    closeDialog() {
      this.dialogVisible = false;
      this.$emit('close');
    },

    resetSelection() {
      this.selectedTemplate = null;
      this.selectedPresentation = null;
    }
  }
}
</script>

<style lang="scss" scoped>
.template-list {
  max-height: 400px;
  overflow-y: auto;
}

.presentation-list {
  max-height: 200px;
  overflow-y: auto;
}

.template-item:hover,
.presentation-item:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.presentation-item.selected {
  background-color: rgba(255, 152, 0, 0.08);
  border-left: 4px solid #ff9800;
}

.template-content {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  max-height: 2.8em;
}

.template-preview {
  border-left: 4px solid #1976d2;
}
</style>
